import React, { useEffect, useRef, useState } from 'react';
import toast from 'react-hot-toast';
import { adminApi } from '../../lib/http';
import {
  PortalButton,
  PortalCard,
  PortalInput,
  PortalModal,
  PortalStatus,
} from '../../components/portal/PortalElements';

function parseQrPayload(value) {
  const trimmed = value.trim();
  if (!trimmed) {
    return { bookingId: '', qrToken: '' };
  }

  try {
    const parsed = JSON.parse(trimmed);
    return {
      bookingId: String(parsed.bookingId || '').trim(),
      qrToken: String(parsed.qrToken || '').trim(),
    };
  } catch (error) {
    return { bookingId: trimmed, qrToken: '' };
  }
}

function normalizeScannerError(error, fallback) {
  const rawMessage = error instanceof Error && error.message ? error.message : fallback;
  const message = rawMessage.toLowerCase();

  if (message.includes('notallowederror') || message.includes('permission denied')) {
    return 'Camera permission was denied. Allow camera access and try again.';
  }
  if (message.includes('notfounderror') || message.includes('requested device not found')) {
    return 'No camera was found on this device. You can upload the QR image instead.';
  }
  if (message.includes('notreadableerror') || message.includes('could not start video source')) {
    return 'The camera is busy in another app or tab. Close it there and try again.';
  }
  if (message.includes('overconstrainederror')) {
    return 'This device could not open the preferred camera. Try another camera or upload the QR image.';
  }
  if (message.includes('invalid or expired qr code')) {
    return 'This QR code is invalid or has expired. Ask the guest to open the latest booking QR.';
  }
  if (message.includes('booking not found')) {
    return 'This booking could not be found. Please confirm the QR belongs to an active reservation.';
  }
  if (message.includes('network error') || message.includes('timeout')) {
    return 'The scanner could not reach the server. Check the connection and try again.';
  }

  return rawMessage;
}

export default function AdminScanner() {
  const videoRef = useRef(null);
  const fileInputRef = useRef(null);
  const streamRef = useRef(null);
  const scanTimerRef = useRef(null);
  const autoStartRef = useRef(false);
  const isSubmittingRef = useRef(false);
  const [manualValue, setManualValue] = useState('');
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [isSecureContext, setIsSecureContext] = useState(true);
  const [isCameraLive, setIsCameraLive] = useState(false);
  const [isUploadingQr, setIsUploadingQr] = useState(false);
  const [isProcessingScan, setIsProcessingScan] = useState(false);
  const [scannerMode, setScannerMode] = useState('Waiting for camera...');
  const [cameraStatus, setCameraStatus] = useState('Opening scanner...');
  const [scannerAlert, setScannerAlert] = useState(null);
  const [cameraFacingMode, setCameraFacingMode] = useState('environment');
  const scannedBooking = result?.booking || (result?.bookingId ? result : null);
  const canCheckIn = scannedBooking && ['pending', 'confirmed'].includes(scannedBooking.status);

  useEffect(() => {
    setIsSecureContext(window.isSecureContext);
    if (!window.isSecureContext) {
      setCameraStatus('Camera access requires HTTPS or localhost. Open this admin panel over a secure origin to prompt for permission.');
    }
  }, []);

  useEffect(() => {
    if (!isSecureContext || autoStartRef.current) {
      return;
    }

    autoStartRef.current = true;
    void startScan();

    return () => {
      stopScan();
    };
  }, [isSecureContext]);

  function stopScan() {
    streamRef.current?.getTracks().forEach((track) => track.stop());
    streamRef.current = null;
    if (scanTimerRef.current) {
      window.clearInterval(scanTimerRef.current);
    }
    scanTimerRef.current = null;
    setIsCameraLive(false);
    setScannerMode('Scanner stopped');
  }

  function showScannerAlert(error, fallback, title = 'Scanner issue') {
    const message = normalizeScannerError(error, fallback);
    stopScan();
    setCameraStatus(message);
    setIsProcessingScan(false);
    setScannerAlert({ title, message });
  }

  async function submitParsedScan(parsed) {
    if (!parsed.bookingId || !parsed.qrToken || isSubmittingRef.current) {
      showScannerAlert(
        new Error('QR code detected, but the secure booking token was missing.'),
        'QR code detected, but the secure booking token was missing.',
        'Invalid QR code',
      );
      return;
    }

    isSubmittingRef.current = true;
    setLoading(true);
    setIsProcessingScan(true);
    setCameraStatus(`QR detected. Confirming booking ${parsed.bookingId}...`);

    try {
      const response = await adminApi.post('/admin/scan-qr', parsed);
      setResult(response.data);
      stopScan();
      setCameraStatus(`QR code scanned successfully for ${parsed.bookingId}.`);
      toast.success('QR verified successfully.');
    } catch (error) {
      setResult(null);
      showScannerAlert(error, 'Unable to verify this QR code.', 'Scan failed');
    } finally {
      isSubmittingRef.current = false;
      setLoading(false);
      setIsProcessingScan(false);
    }
  }

  async function completeCheckIn() {
    if (!scannedBooking?.bookingId || !scannedBooking?.qrToken) {
      toast.error('Validate a booking first.');
      return;
    }

    setLoading(true);
    try {
      const response = await adminApi.post('/admin/check-in', {
        bookingId: scannedBooking.bookingId,
        qrToken: scannedBooking.qrToken,
      });
      setResult(response.data);
      setCameraStatus(`Checked in ${response.data.customerName}.`);
      toast.success(`Checked in ${response.data.customerName}.`);
    } catch (error) {
      showScannerAlert(error, 'Unable to complete check-in.', 'Check-in failed');
    } finally {
      setLoading(false);
    }
  }

  async function submitManual() {
    const parsed = parseQrPayload(manualValue);
    if (!parsed.bookingId || !parsed.qrToken) {
      toast.error('Enter the QR payload in this field so the booking ID and secure token can be verified.');
      return;
    }
    await submitParsedScan(parsed);
  }

  async function detectCodes(source) {
    if (!window.BarcodeDetector) {
      throw new Error('Automatic QR detection is not supported in this browser.');
    }
    const detector = new window.BarcodeDetector({ formats: ['qr_code'] });
    return detector.detect(source);
  }

  function startDetectorLoop() {
    scanTimerRef.current = window.setInterval(async () => {
      if (!videoRef.current || isSubmittingRef.current) {
        return;
      }
      try {
        const codes = await detectCodes(videoRef.current);
        const value = codes[0]?.rawValue;
        if (value) {
          await submitParsedScan(parseQrPayload(value));
        }
      } catch (error) {
        showScannerAlert(error, 'QR detection failed while reading the camera feed.', 'Camera scan failed');
      }
    }, 1200);
  }

  async function startScan() {
    await startScanWithFacingMode(cameraFacingMode);
  }

  async function startScanWithFacingMode(facingMode) {
    try {
      setScannerAlert(null);
      setResult(null);
      stopScan();
      setIsProcessingScan(false);
      setCameraFacingMode(facingMode);
      setScannerMode('Starting camera...');

      if (!window.isSecureContext) {
        showScannerAlert(
          new Error('Camera access is blocked in insecure contexts. Use HTTPS or localhost.'),
          'Camera access is blocked in insecure contexts. Use HTTPS or localhost.',
          'Secure connection required',
        );
        return;
      }

      const mediaDevices = navigator.mediaDevices;
      if (!mediaDevices?.getUserMedia) {
        showScannerAlert(
          new Error('Camera access is not supported in this browser. Please upload a QR image instead.'),
          'Camera access is not supported in this browser. Please upload a QR image instead.',
          'Camera unavailable',
        );
        return;
      }

      setCameraStatus('Requesting camera permission...');
      const stream = await mediaDevices.getUserMedia({ video: { facingMode } });
      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        await videoRef.current.play();
      }
      setIsCameraLive(true);
      setCameraStatus('Point the camera at the QR code.');
      if ('BarcodeDetector' in window) {
        setScannerMode('Live camera with BarcodeDetector');
        startDetectorLoop();
      } else {
        setScannerMode('Manual and upload fallback only');
        setCameraStatus('Camera is active, but this browser does not support automatic QR reading. Use upload or paste fallback.');
      }
    } catch (error) {
      setScannerMode('Camera unavailable');
      showScannerAlert(error, 'Camera access denied or unavailable.', 'Camera unavailable');
    }
  }

  async function toggleCameraFacingMode() {
    const nextFacingMode = cameraFacingMode === 'environment' ? 'user' : 'environment';
    await startScanWithFacingMode(nextFacingMode);
  }

  async function decodeQrFromFile(file) {
    const bitmap = await createImageBitmap(file);
    try {
      const codes = await detectCodes(bitmap);
      const value = codes[0]?.rawValue;
      if (!value) {
        throw new Error('No QR code was detected in the uploaded image.');
      }
      return value;
    } finally {
      bitmap.close?.();
    }
  }

  async function handleQrUpload(event) {
    const file = event.target.files?.[0];
    if (!file) {
      return;
    }

    setScannerAlert(null);
    setIsUploadingQr(true);
    setCameraStatus(`Reading uploaded QR image: ${file.name}`);
    setScannerMode('Processing uploaded QR');

    try {
      const decodedValue = await decodeQrFromFile(file);
      await submitParsedScan(parseQrPayload(decodedValue));
    } catch (error) {
      showScannerAlert(error, 'Unable to process the uploaded QR image.', 'Upload failed');
    } finally {
      event.target.value = '';
      setIsUploadingQr(false);
    }
  }

  return (
    <div className="portal-scanner-shell">
      <PortalCard>
        <div className="portal-section-head">
          <div>
            <p className="portal-kicker">Verification</p>
            <h2>Admin QR scanner</h2>
          </div>
        </div>
        <div className="portal-scanner-badges">
          <div className={`portal-scanner-badge ${isCameraLive ? 'portal-scanner-badge-live' : ''}`}>
            {isCameraLive ? 'Camera live' : 'Camera not live'}
          </div>
          <div className="portal-scanner-badge">{scannerMode}</div>
        </div>
        <div className="portal-scanner-status">{cameraStatus}</div>
        <div className="portal-scanner-stage">
          <video ref={videoRef} className="portal-scanner-video" playsInline muted autoPlay />
          <div className="portal-scanner-switch">
            <PortalButton variant="secondary" onClick={() => void toggleCameraFacingMode()}>
              Switch
            </PortalButton>
          </div>
          {isCameraLive && !isUploadingQr && !scannerAlert && !isProcessingScan ? (
            <div className="portal-scanner-overlay">
              <div className="portal-scanner-frame" />
            </div>
          ) : null}
        </div>
        <div className="portal-action-row">
          <PortalButton onClick={() => void startScan()} disabled={!isSecureContext}>
            Retry Camera
          </PortalButton>
          <PortalButton variant="secondary" onClick={stopScan}>
            Stop
          </PortalButton>
          <PortalButton variant="secondary" onClick={() => fileInputRef.current?.click()} disabled={isUploadingQr}>
            {isUploadingQr ? 'Uploading...' : 'Upload QR'}
          </PortalButton>
        </div>
        <input
          ref={fileInputRef}
          type="file"
          accept="image/png,image/jpeg,image/webp,image/gif"
          className="hidden"
          onChange={handleQrUpload}
        />
      </PortalCard>

      <PortalCard>
        <div className="portal-section-head">
          <div>
            <p className="portal-kicker">Fallback</p>
            <h2>Verify with booking field</h2>
          </div>
        </div>
        <PortalInput
          label="Booking ID"
          value={manualValue}
          onChange={(event) => setManualValue(event.target.value)}
          placeholder='Booking ID or {"bookingId":"KR-...","qrToken":"..."}'
        />
        <div className="portal-action-row">
          <PortalButton onClick={() => void submitManual()} disabled={loading}>
            {loading ? 'Verifying...' : 'Verify QR'}
          </PortalButton>
        </div>
      </PortalCard>

      <PortalModal open={Boolean(scannerAlert)} title={scannerAlert?.title || 'Scanner issue'} onClose={() => setScannerAlert(null)}>
        <p>{scannerAlert?.message}</p>
      </PortalModal>

      <PortalModal
        open={Boolean(scannedBooking)}
        title={scannedBooking ? `Booking ${scannedBooking.bookingId}` : 'Booking'}
        onClose={() => {
          setResult(null);
          void startScan();
        }}
      >
        {scannedBooking ? (
          <div className="portal-stack">
            <div className="portal-action-row">
              <PortalStatus tone={result?.alreadyCheckedIn || scannedBooking.status === 'checked_in' ? 'success' : 'default'}>
                {result?.alreadyCheckedIn || scannedBooking.status === 'checked_in' ? 'Already checked in' : 'Ready for review'}
              </PortalStatus>
            </div>
            <div className="portal-detail-grid">
              <div><strong>Booking</strong><span>{scannedBooking.bookingId}</span></div>
              <div><strong>Guest</strong><span>{scannedBooking.customerName}</span></div>
              <div><strong>Email</strong><span>{scannedBooking.email || '-'}</span></div>
              <div><strong>Phone</strong><span>{scannedBooking.phone || '-'}</span></div>
              <div><strong>Date</strong><span>{scannedBooking.date}</span></div>
              <div><strong>Time</strong><span>{scannedBooking.time}</span></div>
              <div><strong>Guests</strong><span>{scannedBooking.pax}</span></div>
              <div><strong>Table preference</strong><span>{scannedBooking.tablePreference || '-'}</span></div>
              <div><strong>Status</strong><span>{scannedBooking.status}</span></div>
              <div><strong>Payment</strong><span>{scannedBooking.paymentStatus}</span></div>
              <div><strong>Source</strong><span>{scannedBooking.source || '-'}</span></div>
              <div><strong>Booked at</strong><span>{scannedBooking.createdAt ? new Date(scannedBooking.createdAt).toLocaleString() : '-'}</span></div>
            </div>
            {scannedBooking.specialRequest ? (
              <div className="portal-list-item">
                <strong>Special request</strong>
                <p>{scannedBooking.specialRequest}</p>
              </div>
            ) : null}
            <div className="portal-action-row">
              {canCheckIn ? (
                <PortalButton onClick={() => void completeCheckIn()} disabled={loading}>
                  {loading ? 'Checking in...' : 'Mark check in'}
                </PortalButton>
              ) : null}
              <PortalButton
                variant="secondary"
                onClick={() => {
                  setResult(null);
                  void startScan();
                }}
              >
                Scan again
              </PortalButton>
            </div>
          </div>
        ) : null}
      </PortalModal>
    </div>
  );
}
